import {
  basename,
  type ClassifiedTurn,
  classifyTurns,
  fmtDate,
  fmtPct,
  fmtTime,
  fmtTokens,
  fmtUsd,
  PHASES,
  type PriceTable,
  prettyModel,
  type ReportModel,
  rootSessionId,
  type SideEvent,
  type TurnEvent,
  truncate,
  WASTE_EPSILON,
} from "@tktuner/core";
import { c } from "./colors";
import { type Cell, renderTable } from "./table";

export function renderReport(m: ReportModel): string {
  const out: string[] = [];
  const rangeLabel = m.range.days === null ? "all history" : `last ${m.range.days} days`;
  const span = `${m.range.since ? fmtDate(Date.parse(m.range.since)) : "—"} → ${fmtDate(Date.parse(m.range.until))}`;
  const filter = m.range.project ? ` · project ~ "${m.range.project}"` : "";
  out.push(`${c.bold("TkTuner report")} — ${rangeLabel} (${span})${filter}`);

  // Overview
  const o = m.overview;
  const lines: string[] = [];
  lines.push(
    `Sessions ${c.bold(String(o.sessions))} · assistant turns ${c.bold(o.turns.toLocaleString("en-US"))}` +
      (o.subagentTurns > 0 ? ` (${o.subagentTurns.toLocaleString("en-US")} in subagents)` : "") +
      ` · projects ${c.bold(String(o.projects))}`,
  );
  lines.push(
    `Est. cost (API-equivalent): ${c.bold(c.green(fmtUsd(o.totalCostUsd)))}   ` +
      `tokens: ${c.bold(fmtTokens(o.totalTokens))} — in ${fmtTokens(o.tokens.input)} · out ${fmtTokens(o.tokens.output)} · cache read ${fmtTokens(o.tokens.cacheRead)} · cache write ${fmtTokens(o.tokens.cacheWrite)}`,
  );
  if (o.unpricedTurns > 0) {
    lines.push(
      c.yellow(
        `${o.unpricedTurns} turn(s) on unpriced model(s) excluded from cost: ${o.unpricedModels.join(", ")} — update prices.json or use --prices`,
      ),
    );
  }
  out.push(`${header("Overview")}\n${lines.join("\n")}`);

  // By model
  out.push(
    `${header("By model")}\n${renderTable(
      [
        { header: "Model" },
        { header: "Turns", align: "right" },
        { header: "Input", align: "right" },
        { header: "Output", align: "right" },
        { header: "Cache read", align: "right" },
        { header: "Cache write", align: "right" },
        { header: "Est. cost", align: "right" },
        { header: "%", align: "right" },
      ],
      m.byModel.map((r) => [
        prettyModel(r.model),
        String(r.turns),
        fmtTokens(r.input),
        fmtTokens(r.output),
        fmtTokens(r.cacheRead),
        fmtTokens(r.cacheWrite),
        { text: fmtUsd(r.costUsd), paint: r.costUsd === null ? c.yellow : undefined },
        fmtPct(r.costShare),
      ]),
    )}`,
  );

  // Phase × model
  const matrixRows: Cell[][] = m.phaseMatrix.map((row) => {
    const cells: Cell[] = [prettyModel(row.model)];
    for (const phase of PHASES) {
      const cell = row.phases[phase];
      if (cell.turns === 0) cells.push({ text: "·", paint: c.dim });
      else if (cell.wasteUsd > WASTE_EPSILON) {
        cells.push({ text: fmtUsd(cell.costUsd), paint: c.red });
      } else cells.push(fmtUsd(cell.costUsd));
    }
    return cells;
  });
  const wasteLines: string[] = [];
  wasteLines.push(
    `${c.bold(c.red("⚠ waste"))} (premium model on cheap work): ${c.bold(c.red(fmtUsd(m.waste.totalUsd)))}` +
      (m.waste.pctOfSpend !== null ? ` = ${c.bold(fmtPct(m.waste.pctOfSpend))} of spend` : ""),
  );
  wasteLines.push(
    `  exploration on premium: ${fmtUsd(m.waste.explorationUsd)} · execution on premium: ${fmtUsd(m.waste.executionUsd)}` +
      (m.waste.fastModeExtraUsd > WASTE_EPSILON
        ? ` · fast-mode premium: ${fmtUsd(m.waste.fastModeExtraUsd)}`
        : ""),
  );
  out.push(
    `${header("Cost by phase × model")}\n${renderTable(
      [{ header: "Model" }, ...PHASES.map((p) => ({ header: cap(p), align: "right" as const }))],
      matrixRows,
    )}\n\n${wasteLines.join("\n")}`,
  );

  // Cache health
  const ch = m.cache;
  const cacheLines = [
    `Overall hit rate: ${paintHitRate(ch.overall.hitRate)} (read ${fmtTokens(ch.overall.cacheRead)} · write ${fmtTokens(ch.overall.cacheWrite)} · uncached input ${fmtTokens(ch.overall.input)})`,
  ];
  if (ch.compactions.total > 0) {
    cacheLines.push(
      `Compactions: ${ch.compactions.total} across ${ch.compactions.sessionsAffected} session(s) · ~${fmtTokens(ch.compactions.tokensCompacted)} tokens re-summarized`,
    );
  }
  const cacheTable =
    ch.byProject.length > 0
      ? `\n${renderTable(
          [
            { header: "Project (worst hit rate first)" },
            { header: "Turns", align: "right" },
            { header: "Hit rate", align: "right" },
            { header: "Read", align: "right" },
            { header: "Write", align: "right" },
          ],
          ch.byProject.map((p) => [
            basename(p.project),
            String(p.turns),
            {
              text: fmtPct(p.hitRate),
              paint: p.hitRate !== null && p.hitRate < 0.6 ? c.yellow : undefined,
            },
            fmtTokens(p.cacheRead),
            fmtTokens(p.cacheWrite),
          ]),
        )}`
      : "";
  out.push(`${header("Cache health")}\n${cacheLines.join("\n")}${cacheTable}`);

  // Top sessions
  if (m.topSessions.length > 0) {
    out.push(
      `${header("Top 5 most wasteful sessions")}\n${renderTable(
        [
          { header: "#", align: "right" },
          { header: "Session" },
          { header: "Project" },
          { header: "Date" },
          { header: "Turns", align: "right" },
          { header: "Cost", align: "right" },
          { header: "Waste", align: "right" },
          { header: "Dominant anti-pattern" },
        ],
        m.topSessions.map((s, i) => [
          String(i + 1),
          s.label,
          s.project,
          s.date,
          String(s.turns),
          fmtUsd(s.costUsd),
          { text: fmtUsd(s.wasteUsd), paint: s.wasteUsd > WASTE_EPSILON ? c.red : undefined },
          s.antiPattern,
        ]),
      )}\n${c.dim(`  drill down: tktuner report --debug-session <session>`)}`,
    );
  }

  // Subagent leaks
  if (m.subagentLeaks.length > 0) {
    out.push(
      `${header("Subagent leaks (premium model in delegated runs)")}\n${renderTable(
        [
          { header: "Agent type" },
          { header: "Model" },
          { header: "Runs", align: "right" },
          { header: "Est. cost", align: "right" },
        ],
        m.subagentLeaks.map((l) => [
          l.agentType,
          prettyModel(l.model),
          String(l.runs),
          { text: fmtUsd(l.costUsd), paint: c.red },
        ]),
      )}\n${c.dim("  fix: pin model: haiku|sonnet in the agent frontmatter (.claude/agents/*.md)")}`,
    );
  } else {
    out.push(`${header("Subagent leaks")}\n${c.green("No subagent runs on premium models.")}`);
  }

  // Quota pressure
  const maxWindowCost = Math.max(...m.quota.last7d.map((w) => w.costUsd), 0);
  const quotaParts: string[] = [];
  if (m.quota.last7d.length > 0) {
    quotaParts.push(
      renderTable(
        [
          { header: `5h window start (last 7d)` },
          { header: "Turns", align: "right" },
          { header: "Tokens", align: "right" },
          { header: "Cost-equivalent", align: "right" },
          { header: "" },
        ],
        m.quota.last7d.map((w) => [
          w.start,
          String(w.turns),
          fmtTokens(w.totalTokens),
          fmtUsd(w.costUsd),
          { text: bar(w.costUsd, maxWindowCost, 14), paint: c.cyan },
        ]),
      ),
    );
  }
  const maxWeekCost = Math.max(...m.quota.weekly.map((w) => w.costUsd), 0);
  if (m.quota.weekly.length > 0) {
    quotaParts.push(
      renderTable(
        [
          { header: "Week" },
          { header: "Turns", align: "right" },
          { header: "Tokens", align: "right" },
          { header: "Cost-equivalent", align: "right" },
          { header: "" },
        ],
        m.quota.weekly.map((w) => [
          w.week,
          String(w.turns),
          fmtTokens(w.totalTokens),
          fmtUsd(w.costUsd),
          { text: bar(w.costUsd, maxWeekCost, 14), paint: c.magenta },
        ]),
      ),
    );
  }
  quotaParts.push(c.dim(`  ${m.quota.note}`));
  out.push(`${header("Quota pressure")}\n${quotaParts.join("\n\n")}`);

  // Recommendations
  if (m.recommendations.length > 0) {
    out.push(
      `${header("Recommendations")}\n${m.recommendations
        .map((r, i) => `  ${c.green(`${i + 1}.`)} ${r}`)
        .join("\n")}`,
    );
  }

  out.push(
    c.dim(
      [
        m.waste.assumption,
        `Prices v${m.priceTableVersion} (platform.claude.com) — override with --prices <file>.`,
      ].join("\n"),
    ),
  );
  return `\n${out.join("\n\n")}\n`;
}

export function renderDebugSession(
  turns: TurnEvent[],
  events: SideEvent[],
  prices: PriceTable,
  idPrefix: string,
): string {
  const match = turns.filter(
    (t) =>
      t.sessionId.startsWith(idPrefix) ||
      rootSessionId(t).startsWith(idPrefix) ||
      t.slug?.startsWith(idPrefix),
  );
  if (match.length === 0) {
    return c.yellow(`No turns found for session prefix "${idPrefix}".`);
  }
  const classified = classifyTurns(match, events).sort(
    (a, b) => a.turn.timestamp - b.turn.timestamp,
  );
  const first = classified[0] as ClassifiedTurn;
  const totalCost = classified.reduce((acc, ct) => acc + (prices.turnCostUsd(ct.turn) ?? 0), 0);
  const sessions = new Set(match.map((t) => t.sessionId));

  const head =
    `${c.bold("Session debug")} — ${idPrefix} (${sessions.size} session file(s), ${match.length} turns)\n` +
    `project ${first.turn.projectPath || "—"} · ${fmtDate(first.turn.timestamp)} · est. cost ${c.bold(fmtUsd(totalCost))}`;

  const table = renderTable(
    [
      { header: "Time" },
      { header: "Model" },
      { header: "Raw", align: "left" },
      { header: "Smoothed", align: "left" },
      { header: "Think", align: "right" },
      { header: "Out", align: "right" },
      { header: "Cost", align: "right" },
      { header: "Tools" },
    ],
    classified.map((ct) => [
      fmtTime(ct.turn.timestamp),
      prettyModel(ct.turn.model),
      ct.phase,
      ct.phase === ct.phaseSmoothed
        ? ct.phaseSmoothed
        : { text: ct.phaseSmoothed, paint: c.yellow },
      fmtTokens(ct.turn.thinkingChars),
      fmtTokens(ct.turn.usage.output),
      fmtUsd(prices.turnCostUsd(ct.turn)),
      truncate(ct.turn.tools.join(","), 40) || c.dim("—"),
    ]),
  );

  const sessionEvents = events
    .filter((e) => sessions.has(e.sessionId) && e.kind !== "turn_duration" && e.timestamp)
    .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
    .map((e) => `  ${fmtTime(e.timestamp ?? 0)}  ${e.kind}`)
    .join("\n");

  return `\n${head}\n\n${table}${sessionEvents ? `\n\n${c.bold("Events")}\n${sessionEvents}` : ""}\n`;
}

function header(title: string): string {
  const line = "━".repeat(Math.max(4, 62 - title.length));
  return c.bold(c.cyan(`━━ ${title} ${line.slice(0, Math.max(2, 58 - title.length))}`));
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function paintHitRate(rate: number | null): string {
  const text = fmtPct(rate);
  if (rate === null) return text;
  if (rate >= 0.8) return c.green(c.bold(text));
  if (rate >= 0.6) return c.yellow(c.bold(text));
  return c.red(c.bold(text));
}

function bar(value: number, max: number, width: number): string {
  if (max <= 0 || value <= 0) return "";
  return "█".repeat(Math.max(1, Math.round((value / max) * width)));
}
